import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { format } from 'date-fns';
import { Ride } from '../../types';
import toast from 'react-hot-toast';

interface MyRide extends Ride {
  checkedIn?: boolean;
  joinedAt?: string;
  isCompleted?: boolean;
}

const MyRidesPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState<MyRide[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'past'>('all');

  useEffect(() => {
    fetchMyRides();
  }, [statusFilter]);

  const fetchMyRides = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rides/my/rides', {
        params: { status: statusFilter },
      });
      // API returns rides directly with participant info
      const ridesData = response.data.rides || [];
      setRides(ridesData);
    } catch (error) {
      console.error('Error fetching my rides:', error);
      toast.error(t('rides.myRidesError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMyRides();
  };

  const getRideStatus = (ride: MyRide) => {
    const now = new Date();
    // Fix timezone issue: combine date and time in local timezone
    // departureDate from API is an ISO string like "2025-12-24T00:00:00.000Z" or "2025-12-24"
    // departureTime is like "07:00"
    if (!ride.departureDate) {
      return { label: t('rides.statusUpcoming'), color: 'bg-primary-100 text-primary-800' };
    }
    
    const timeStr = ride.departureTime || '00:00';
    // Extract date part from ISO string (handles both "2025-12-24T00:00:00.000Z" and "2025-12-24")
    const dateStr = String(ride.departureDate).split('T')[0]; // Get "YYYY-MM-DD" from ISO string
    const dateTimeString = `${dateStr}T${timeStr}`;
    const departureDate = new Date(dateTimeString);
    
    if (isNaN(departureDate.getTime())) {
      console.warn('Invalid date for ride:', ride.id, dateTimeString);
      return { label: t('rides.statusUpcoming'), color: 'bg-primary-100 text-primary-800' };
    }
    
    // Check if user is a participant and get their check-in status
    const userParticipant = ride.participants?.find((p: any) => p.userId === user?.id);
    const checkedIn = userParticipant?.checkedIn || false;
    
    if (ride.isCompleted) {
      return { label: t('rides.statusCompleted'), color: 'bg-green-100 text-green-800' };
    } else if (checkedIn) {
      return { label: t('rides.statusCheckedIn'), color: 'bg-blue-100 text-blue-800' };
    } else if (departureDate < now) {
      return { label: t('rides.statusNoShow'), color: 'bg-red-100 text-red-800' };
    } else {
      return { label: t('rides.statusUpcoming'), color: 'bg-primary-100 text-primary-800' };
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  if (loading && rides.length === 0) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-lg">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/rides')}
          className="text-primary-600 hover:text-primary-700 mb-4"
        >
          ← {t('common.back')}
        </button>
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('rides.myRides')}</h1>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
          >
            {refreshing ? t('common.loading') : t('common.refresh')}
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusFilter === 'all'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('rides.all')}
        </button>
        <button
          onClick={() => setStatusFilter('upcoming')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusFilter === 'upcoming'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('rides.upcoming')}
        </button>
        <button
          onClick={() => setStatusFilter('past')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            statusFilter === 'past'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('rides.past')}
        </button>
      </div>

      {/* Rides List */}
      {rides.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <div className="text-6xl mb-4">🚴</div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('rides.myRidesEmptyTitle')}</h3>
          <p className="text-gray-600 mb-6">
            {statusFilter === 'all'
              ? t('rides.myRidesEmptyAll')
              : statusFilter === 'upcoming'
              ? t('rides.myRidesEmptyUpcoming')
              : t('rides.myRidesEmptyPast')}
          </p>
          <Link
            to="/rides"
            className="inline-block px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            {t('rides.browseRides')}
          </Link>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rides.map((ride) => {
            const statusInfo = getRideStatus(ride);
            
            return (
              <Link
                key={ride.id}
                to={`/rides/${ride.id}`}
                className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 flex-1">{ride.name}</h2>
                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>{formatDate(ride.departureDate)}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="truncate">{ride.departureLocation}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                    <span>{ride.distanceKm} km</span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex items-center text-sm text-gray-600">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    <span>
                      {ride.participantCount || 0} / {ride.maxRiders} {t('rides.participants')}
                    </span>
                  </div>
                  {ride.cost > 0 && (
                    <span className="text-sm font-semibold text-primary-600">
                      ${ride.cost.toFixed(2)}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyRidesPage;
